import { useEffect, useId, useRef, type ReactNode } from 'react';
import { percent as formatPercentValue } from '../lib/format';
import { progressTone } from '../lib/progress';

/**
 * Pezzi di interfaccia condivisi.
 *
 * Stanno tutti qui perché sono le cose che devono restare identiche in ogni
 * schermata: il modo di dire "restituito", la forma di una barra di
 * avanzamento, il comportamento di una finestra modale. Duplicarli è il modo
 * più rapido per farli divergere.
 */

/** Spunta piena se restituito, cerchio vuoto se mancante. */
export function StateMark({
  returned,
  withLabel = true,
}: {
  returned: boolean;
  withLabel?: boolean;
}) {
  return (
    <span className={`state ${returned ? 'state--returned' : 'state--missing'}`}>
      <span className="state__mark" aria-hidden="true">
        {returned ? (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8.6 L6.4 12 L13 4.5"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </span>
      {withLabel && <span>{returned ? 'Restituito' : 'Mancante'}</span>}
    </span>
  );
}

/**
 * Barra di avanzamento.
 *
 * Il colore segue quanto è piena — rossa sotto metà, ambra da metà in su, verde
 * a fine gara: la regola sta in `lib/progress.ts`, perché ha dei confini da
 * verificare e non è un disegno. La lunghezza resta il segnale principale.
 */
export function ProgressBar({
  fraction,
  large = false,
  label,
}: {
  fraction: number;
  large?: boolean;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(1, fraction));
  const tone = progressTone(clamped);

  return (
    <div
      className={`progress ${large ? 'progress--lg' : ''} progress--${tone}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped * 100)}
      aria-label={label ?? 'Avanzamento delle restituzioni'}
    >
      <div className="progress__fill" style={{ width: `${clamped * 100}%` }} />
    </div>
  );
}

export function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone?: 'ok' | 'warn';
}) {
  return (
    <div className={`stat ${tone ? `stat--${tone}` : ''}`}>
      <div className="label">{label}</div>
      <div className="stat__value">{value}</div>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, 'aria-describedby': describedBy })}
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function PercentText({ fraction, digits = 1 }: { fraction: number; digits?: number }) {
  return <span className="mono">{formatPercentValue(fraction, digits)}</span>;
}

/**
 * Finestra modale.
 *
 * Tiene il fuoco dentro mentre è aperta e lo restituisce a chi l'aveva aperta
 * quando si chiude: senza questo, chi naviga da tastiera si ritrova a tabulare
 * nella pagina sottostante senza accorgersene.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
  labelledBy,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  labelledBy?: string;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  /**
   * `onClose` sta in una ref, non fra le dipendenze dell'effetto.
   *
   * Chi apre la finestra passa quasi sempre una funzione scritta lì per lì, che
   * cambia identità a ogni render del genitore — e il genitore si ri-renderizza
   * a ogni messaggio del socket. Con `onClose` fra le dipendenze l'effetto si
   * rifarebbe ogni volta: il fuoco tornerebbe all'inizio del modulo mentre
   * l'operatore sta scrivendo nel terzo campo.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;

    // Il primo campo, non il primo elemento in assoluto: aprire una finestra e
    // dover tabulare oltre il pulsante di chiusura è una piccola seccatura che
    // si sente ogni volta.
    const focusable = boxRef.current?.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, select, button:not([data-skip-autofocus])',
    );
    (focusable ?? boxRef.current)?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus?.();
    };
  }, []);

  return (
    <div className="overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={boxRef}
        className={`modal ${wide ? 'modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy ?? titleId}
        tabIndex={-1}
      >
        <div className="modal__head">
          <h2 className="modal__title" id={labelledBy ?? titleId}>
            {title}
          </h2>
          <span className="shell__spacer" />
          <button
            type="button"
            className="btn btn--sm btn--quiet"
            onClick={onClose}
            aria-label="Chiudi"
            data-skip-autofocus
          >
            ✕
          </button>
        </div>

        <div className="modal__body">{children}</div>
        {footer && <div className="modal__foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Riquadro di errore/avviso, con il tono giusto per il contesto. */
export function Notice({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'danger' | 'missing' | 'returned';
  children: ReactNode;
}) {
  return (
    <div className={`notice ${tone === 'neutral' ? '' : `notice--${tone}`}`} role={tone === 'danger' ? 'alert' : undefined}>
      <div>{children}</div>
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div style={{ padding: '32px 18px', textAlign: 'center' }} className="muted">
      <p style={{ fontWeight: 650, color: 'var(--ink)' }}>{title}</p>
      {hint && <p style={{ marginTop: 6 }}>{hint}</p>}
    </div>
  );
}
